import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const recipes = [
  {
    id: 1,
    name: 'Classic Espresso Martini',
    difficulty: 'Beginner',
    time: '5 min',
    image: 'https://public.readdy.ai/ai/img_res/c6c0f4e52488b24653c2d2d5c02a6503.jpg',
    ingredients: ['50ml Vodka', '30ml Coffee Liqueur', '25ml Fresh Espresso', '10ml Sugar Syrup'],
    steps: [
      'Brew a fresh shot of espresso and let it cool slightly for 30 seconds.',
      'Add vodka, coffee liqueur, espresso, and sugar syrup to a cocktail shaker.',
      'Fill the shaker with ice and shake vigorously for 15 seconds until frothy.',
      'Double strain into a chilled coupe glass for a silky smooth finish.',
      'Garnish with three coffee beans placed in the centre of the foam.',
    ],
    tip: 'The key to a perfect foam is shaking hard — the espresso creates that beautiful crema layer.',
  },
  {
    id: 2,
    name: 'Spiced Old Fashioned',
    difficulty: 'Intermediate',
    time: '4 min',
    image: 'https://readdy.ai/api/search-image?query=premium%20old%20fashioned%20whiskey%20cocktail%20in%20crystal%20rocks%20glass%20with%20large%20ice%20sphere%20and%20orange%20peel%20garnish%2C%20warm%20amber%20lighting%20on%20dark%20wooden%20bar%20surface%2C%20professional%20cocktail%20photography%2C%20luxurious%20moody%20atmosphere&width=400&height=500&seq=learn-oldfash-001&orientation=portrait',
    ingredients: ['60ml Bourbon Whiskey', '10ml Demerara Syrup', '2 dashes Angostura Bitters', 'Orange Peel'],
    steps: [
      'Place a large ice sphere or cube into a crystal rocks glass.',
      'Add demerara syrup and angostura bitters directly into the glass.',
      'Pour bourbon over the ice and stir gently for 20 seconds to chill and dilute.',
      'Express an orange peel over the drink by squeezing it skin-side down.',
      'Drop the peel into the glass and serve immediately.',
    ],
    tip: 'Use a large ice sphere — it melts slower and keeps your drink perfectly balanced longer.',
  },
  {
    id: 3,
    name: 'Passion Fruit Mojito',
    difficulty: 'Beginner',
    time: '6 min',
    image: 'https://readdy.ai/api/search-image?query=vibrant%20passion%20fruit%20mojito%20cocktail%20in%20tall%20highball%20glass%20with%20fresh%20mint%20leaves%20and%20passion%20fruit%20half%20garnish%2C%20tropical%20warm%20lighting%20on%20elegant%20bar%20surface%2C%20professional%20cocktail%20photography%2C%20refreshing%20summer%20drink%20aesthetic&width=400&height=500&seq=learn-mojito-001&orientation=portrait',
    ingredients: ['50ml White Rum', '1 Fresh Passion Fruit', '8 Mint Leaves', '25ml Lime Juice', '20ml Sugar Syrup', 'Soda Water'],
    steps: [
      'Scoop the pulp from a fresh passion fruit into a cocktail shaker.',
      'Add mint leaves and gently muddle to release the oils — don\'t crush them.',
      'Add rum, lime juice, and sugar syrup, then fill with ice.',
      'Shake well for 10 seconds and strain into a tall glass filled with crushed ice.',
      'Top with soda water and garnish with a passion fruit half and mint sprig.',
    ],
    tip: 'Gentle muddling is everything — bruised mint turns bitter. Just press and twist.',
  },
  {
    id: 4,
    name: 'Smoky Margarita',
    difficulty: 'Advanced',
    time: '7 min',
    image: 'https://readdy.ai/api/search-image?query=smoky%20mezcal%20margarita%20cocktail%20in%20salt%20rimmed%20glass%20with%20lime%20wheel%20and%20smoke%20wisps%2C%20dramatic%20dark%20moody%20bar%20background%20with%20warm%20amber%20lighting%2C%20professional%20cocktail%20photography%2C%20luxurious%20sophisticated%20atmosphere&width=400&height=500&seq=learn-marg-001&orientation=portrait',
    ingredients: ['30ml Mezcal', '30ml Tequila Blanco', '25ml Fresh Lime Juice', '20ml Agave Syrup', 'Smoked Salt'],
    steps: [
      'Run a lime wedge around the rim of a rocks glass and dip into smoked salt.',
      'Combine mezcal, tequila, lime juice, and agave syrup in a shaker with ice.',
      'Shake vigorously for 12 seconds until the shaker is frosty cold.',
      'Strain over a large ice cube in your salt-rimmed glass.',
      'Garnish with a lime wheel and optionally smoke with a rosemary sprig for drama.',
    ],
    tip: 'The mezcal brings the smoke — if you want extra drama, torch a rosemary sprig tableside.',
  },
];

const difficultyColor: Record<string, string> = {
  Beginner: 'bg-emerald-500',
  Intermediate: 'bg-amber-500',
  Advanced: 'bg-rose-500',
};

export default function LearnToMix() {
  const [activeRecipe, setActiveRecipe] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const recipe = recipes[activeRecipe];
  
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: contentRef, isVisible: contentVisible } = useScrollAnimation({ rootMargin: '0px 0px -100px 0px' });
  const { ref: statsRef, isVisible: statsVisible } = useScrollAnimation();

  return (
    <section id="learn" className="py-28 bg-gradient-to-b from-white to-stone-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div 
          ref={headerRef}
          className={`text-center mb-16 scroll-animate ${headerVisible ? 'visible' : ''}`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-50 rounded-full mb-4">
            <i className="ri-goblet-fill text-amber-500 text-xs"></i>
            <span className="text-amber-700 text-xs font-semibold tracking-widest uppercase">
              Learn Step by Step
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-4">
            How to Make <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-yellow-400">Cocktails</span>
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto">
            Follow our signature step-by-step tutorials. Each recipe is broken down so you can master it at home — from your very first shake to advanced techniques.
          </p>
        </div>

        {/* Recipe Selector Tabs */}
        <div className={`flex flex-wrap justify-center gap-3 mb-14 scroll-animate ${headerVisible ? 'visible' : ''}`} style={{ transitionDelay: '200ms' }}>
          {recipes.map((r, i) => (
            <button
              key={r.id}
              onClick={() => { setActiveRecipe(i); setActiveStep(0); }}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeRecipe === i
                  ? 'bg-gradient-to-r from-amber-400 to-yellow-300 text-amber-900 shadow-lg shadow-amber-500/20'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {r.name}
            </button>
          ))}
        </div>

        {/* Recipe Detail */}
        <div 
          ref={contentRef}
          className="grid lg:grid-cols-2 gap-12 items-start"
        >
          {/* Left — Image & Ingredients */}
          <div className={`space-y-6 scroll-animate-left ${contentVisible ? 'visible' : ''}`}>
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <div className="w-full h-[520px]">
                <img
                  src={recipe.image}
                  alt={recipe.name}
                  className="w-full h-full object-cover object-top transition-all duration-700"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="flex items-center gap-3 mb-3">
                  <span className={`px-3 py-1 ${difficultyColor[recipe.difficulty]} text-white text-xs font-semibold rounded-full`}>
                    {recipe.difficulty}
                  </span>
                  <span className="px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-medium rounded-full flex items-center gap-1">
                    <i className="ri-time-line"></i> {recipe.time}
                  </span>
                </div>
                <h3 className="text-3xl font-serif font-bold text-white">{recipe.name}</h3>
              </div>
            </div>

            {/* Ingredients Card */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-amber-100/50">
              <h4 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-4">
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                  <i className="ri-flask-line text-amber-600 text-sm"></i>
                </div>
                Ingredients
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {recipe.ingredients.map((ing, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                    <div className="w-2 h-2 rounded-full bg-gradient-to-r from-amber-400 to-yellow-300 flex-shrink-0"></div>
                    {ing}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right — Steps */}
          <div className={`space-y-6 scroll-animate-right ${contentVisible ? 'visible' : ''}`}>
            <h4 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                <i className="ri-list-ordered text-amber-600 text-sm"></i>
              </div>
              Step-by-Step Instructions
            </h4>

            <div className="space-y-4">
              {recipe.steps.map((step, i) => (
                <button
                  key={i}
                  onClick={() => setActiveStep(i)}
                  className={`w-full text-left group rounded-2xl p-5 transition-all duration-300 cursor-pointer ${
                    activeStep === i
                      ? 'bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-300 shadow-md shadow-amber-100'
                      : 'bg-white border border-gray-100 hover:border-amber-200 hover:shadow-sm'
                  }`}
                >
                  <div className="flex gap-4">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm transition-all ${
                        activeStep === i
                          ? 'bg-gradient-to-br from-amber-400 to-yellow-300 text-amber-900 shadow-lg shadow-amber-500/20'
                          : i < activeStep
                          ? 'bg-emerald-100 text-emerald-600'
                          : 'bg-gray-100 text-gray-400'
                      }`}
                    >
                      {i < activeStep ? (
                        <i className="ri-check-line"></i>
                      ) : (
                        i + 1
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-medium text-amber-600 mb-1 uppercase tracking-wider">
                        Step {i + 1}
                      </p>
                      <p className={`text-sm leading-relaxed ${activeStep === i ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>
                        {step}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Pro Tip */}
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-2xl p-6 border border-amber-200/50">
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-yellow-300 flex items-center justify-center flex-shrink-0 shadow-lg shadow-amber-500/20">
                  <i className="ri-lightbulb-flash-line text-amber-900 text-xl"></i>
                </div>
                <div>
                  <p className="text-sm font-bold text-amber-800 mb-1">Pro Tip from House of FMR</p>
                  <p className="text-sm text-amber-700 leading-relaxed">{recipe.tip}</p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="bg-gray-900 rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <p className="text-white font-semibold mb-1">Want to learn this in person?</p>
                <p className="text-gray-400 text-sm">Book a class and our experts will teach you hands-on.</p>
              </div>
              <button
                onClick={() => {
                  const el = document.getElementById('booking');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                className="px-6 py-3 bg-gradient-to-r from-amber-400 to-yellow-300 text-amber-900 text-sm font-bold rounded-full hover:from-amber-300 hover:to-yellow-200 transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-amber-500/20"
              >
                Book a Class
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Stats */}
        <div 
          ref={statsRef}
          className={`mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 stagger-children ${statsVisible ? 'visible' : ''}`}
        >
          {[
            { icon: 'ri-book-open-line', number: '200+', label: 'Recipes to Learn' },
            { icon: 'ri-video-line', number: '50+', label: 'Video Tutorials' },
            { icon: 'ri-user-star-line', number: '500+', label: 'Students Taught' },
            { icon: 'ri-star-line', number: '4.9', label: 'Average Rating' },
          ].map((stat, i) => (
            <div key={i} className="text-center bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md hover:border-amber-200 transition-all cursor-default group">
              <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-3 group-hover:bg-gradient-to-br group-hover:from-amber-400 group-hover:to-yellow-300 transition-all">
                <i className={`${stat.icon} text-xl text-amber-600 group-hover:text-amber-900 transition-colors`}></i>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stat.number}</p>
              <p className="text-sm text-gray-500">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
